import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { APP_PATH } from "../api/backendPath";

// Sentinel value for the "type my own reason" dropdown option. Not a real
// reason id, so it can never collide with one coming from the API.
export const CUSTOM_REJECTION_REASON = "__custom__";

/**
 * useRejectionReasons — loads the admin-configured, active reject-order reasons
 * for this center and exposes them ready for the Reject Order dropdown.
 *
 * The reasons come back bilingual; `label` picks the Arabic text when the app
 * locale is Arabic and falls back to English otherwise (including Kurdish,
 * which has no dedicated reason column).
 *
 * @param {string} serviceType  This center's type, e.g. "lab" / "imaging".
 * @param {boolean} enabled     Fetch only when true (e.g. when the modal opens).
 */
export default function useRejectionReasons(serviceType, enabled = true) {
  const { i18n } = useTranslation();
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);

  const isArabic = (i18n?.language || "").toLowerCase().startsWith("ar");

  const fetchReasons = useCallback(async () => {
    if (!serviceType) return;
    setLoading(true);
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      if (!token) return;
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}${APP_PATH.providerRejectionReasons}`,
        {
          params: { service_type: serviceType },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setReasons(res?.data?.data?.reasons || []);
    } catch (e) {
      // Non-blocking: if the list can't be loaded the officer can still type a
      // custom reason, so fail quiet rather than blocking the reject flow.
      setReasons([]);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  useEffect(() => {
    if (enabled) fetchReasons();
  }, [enabled, fetchReasons]);

  // Dropdown-ready options, already ordered by the API (display_order ASC).
  const options = useMemo(
    () =>
      reasons.map((reason) => ({
        value: String(reason.id),
        label:
          (isArabic ? reason.reason_arabic : reason.reason_english) ||
          reason.reason_english ||
          reason.reason_arabic,
      })),
    [reasons, isArabic],
  );

  /**
   * Resolve a selected dropdown value to the text to submit. Returns null for
   * the custom option (the caller submits the free-text box instead).
   */
  const resolveReasonText = useCallback(
    (selectedValue) => {
      if (!selectedValue || selectedValue === CUSTOM_REJECTION_REASON)
        return null;
      return options.find((o) => o.value === String(selectedValue))?.label || null;
    },
    [options],
  );

  return { options, loading, refresh: fetchReasons, resolveReasonText };
}
